import { Org, OrgLite } from 'types/org';
import {
  AllocationCRUDSchema,
  ConsumeStockEvent,
  ExpandedShipments,
  RelatedRecord,
  ShipmentLineLiteExpandedStockLot,
  StockLocation,
  StockLotCRUDSchema,
  Supplier,
} from 'types/inventory';
import { BomCRUDSchema, BomLineLite, BomLite } from 'types/bom';
import { User, UserLite } from 'types/user';
import {
  NoPartInventory,
  Part,
  PartCore,
  PartId,
  PartMarket,
  PartSpecs,
  PartSupply,
  Solicitation,
} from 'types/part';
import {
  KittingEstimate,
  LineItem,
  PriceModelLite,
  SnapshotLine,
} from 'types/purchasing';
import { Order, OrderLineLite, OrderSnapshotLine } from 'types/order';
import { PartRuleSet } from 'types/partRules';
import { TeamLite } from 'types/properties';
import { Lite } from 'types/api';
import {
  PurchaseRequest,
  PurchaseOrderLineServerSideSubSchema,
} from 'types/procurement';
import { CustomPropertiesType, DateTimeString } from 'types';
import { OrgPartType, V2OrgPartTypes } from 'types/clientV2/parts';
import { ProgressStackProps } from 'ui-component/ProgressStack';
import { PartSupplyRetryStatus } from 'types/partStatus';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import { KitRequestLineStats } from 'hooks/useGetDecoratedKitRequestLines/utils';
import {
  IconAlertTriangle,
  IconBox,
  IconBuildingWarehouse,
  IconCheck,
  IconFileCheck,
  IconInbox,
  IconLoader,
  IconMailShare,
  IconPackage,
  IconSend,
  IconShoppingCart,
  IconShoppingCartQuestion,
  IconTransferIn,
  IconTrolley,
  IconTruckDelivery,
  IconX,
} from '@tabler/icons-react';
import {
  PurchaseOrderOrderSubtype,
  PurchaseOrderLineOrderSubtype,
  ShipmentLineOrderSubtype,
  SupplierQuoteOrderSubtype,
} from 'views/simplifiedPurchasing/Order';
import {
  PurchaseRequestGroupSchemaLite,
  PurchaseRequestSchema,
} from 'types/clientV2/purchaseRequests';
import { SupplierQuoteLineSchemaLite } from 'types/sourcing';
import { KitRequestLineSchemaLite, KitRequestSchemaLite } from 'types/kitting';
import { AllocationSchema } from 'types/clientV2/allocations';
import { ChipColor } from 'ui-component/extended/Chip';

export type ProductionRun = {
  id: string;
  status: string;
  name: string | null;
  referenceNumber: string;
  notificationEmail: string | null;
  notes: string;
  maxLead: number | null;
  requiredCertifications: string[];
  approvedSuppliers: string[] | Supplier[];
  limitToFacilities: StockLocation[] | null;
  archived: boolean;
  locked: boolean;
  createdAt: DateTimeString;
  approvedAt: string | null;
  allocatedAt: string | null;
  executedAt: string | null;
  // eslint-disable-next-line no-restricted-syntax -- API field name from external source
  cancelledAt: string | null;
  productionDate: string | null;
  autoGenerateAllocations: boolean;
  autoGenerateKitting: boolean;
  autoApproveKitting: boolean;
  autoExecute: boolean;
  authorizedOnly: boolean;
  addOverage: boolean;
  partRuleSet: PartRuleSet['id'];
  addOveragePerBom: boolean;
  addMultiKitOverage: number;
  noBackorder: boolean;
  avoidBackorder: boolean;
  includeExpectedStock: boolean;
  includeProcessingStock: boolean;
  includeOnhandStock: boolean;
  addRefdesNote: boolean;
  addBomLineNumberNote: boolean;
  lotMatchingPrioritization: string;
  org: OrgLite;
  approvedBy: UserLite;
  executedBy: UserLite;
  // eslint-disable-next-line no-restricted-syntax -- API field name from external source
  cancelledBy: UserLite;
  productionLocation: StockLocation | null;
  lines: ProductionRunLinesLite[];
  relatedRecords: RelatedRecord[];
  globalNotePrefix: string;
  globalNoteSuffix: string;
  defaultShippingService: string;
  defaultShippingCarrier: string;
  externalMetadata: {
    rowData?: { [key: string]: { [key: string]: string | number | null } };
  };
  team?: TeamLite | null;
  customProperties: CustomPropertiesType;
  createdBy: UserLite | null;
};

export type ProductionRunLite = Lite<ProductionRun, 'customProperties'>;

export type ProductionRunLines = {
  id: string;
  quant?: number;
  bom: BomLite;
  productionRun: ProductionRunLite;
};

export type ProductionRunLinesLite = Lite<ProductionRunLines, 'bom'>;

export type ProductionRunLineCreate = {
  bom: BomCRUDSchema['id'];
  quant?: number;
};

export type ProductionRunLineUpdate = {
  quant: number;
};

export type ProductionRunsRequest = {
  status?: string;
  productionLocationId?: StockLocation['id'];
  productionRunId?: ProductionRun['id'];
  bomId?: BomCRUDSchema['id'];
};

export type ProductionRunUpdate = {
  notes?: string;
  locked?: boolean;
  archived?: boolean;
  executed?: boolean;
  name?: string;
  referenceNumber?: string;
  productionDate?: string | null;
  productionLocation?: StockLocation['id'];
  autoGenerateAllocations?: boolean;
  autoGenerateKitting?: boolean;
  autoApproveKitting?: boolean;
  autoExecute?: boolean;
  globalNotePrefix?: string;
  globalNoteSuffix?: string;
  addBomLineNumberNote?: boolean;
  addOverage?: boolean;
  partRuleSet?: PartRuleSet['id'];
  addOveragePerBom?: boolean;
  addMultiKitOverage?: number;
  maxLead?: number;
  authorizedOnly?: boolean;
  requiredCertifications?: string[];
  approvedSuppliers?: string[];
  defaultShippingService?: string;
  defaultShippingCarrier?: string;
  bom?: BomCRUDSchema['id'] | null;
  noBackorder?: boolean;
  avoidBackorder?: boolean;
  externalMetadata?: object;
  internalMetadata?: object;
  customProperties?: object;
  team?: TeamLite['id'] | null;
};

export type ProductionRunCreate = {
  name: string;
  referenceNumber: string;
  productionDate?: string | null;
  notes?: string;
  productionLocation: StockLocation['id'];
  autoGenerateAllocations?: boolean;
  autoGenerateKitting?: boolean;
  autoApproveKitting?: boolean;
  autoExecute?: boolean;
  bom?: BomCRUDSchema['id'] | null;
  quant?: number;
  externalMetadata?: object;
  internalMetadata?: object;
  customProperties?: object;
  team?: TeamLite['id'] | null;
};

export type ProductionRunSnapshot = {
  id: string;
  lead: number;
  total: number;
  landedPrice: number;
  approved: boolean;
  summary: LineItem[];
  details: LineItem[];
  org: Org['id'] | Org;
  priceModel: PriceModelLite['id'] | PriceModelLite;
  submittedBy: User['id'] | User;
  approvedBy: null | User['id'] | User;
  expired: boolean;
  purchased: boolean;
  lines: SnapshotLine['id'][] | SnapshotLine[];
  relatedRecord: string;
  kittingEstimate?: KittingEstimate;
};

export type ProductionRunConsumption = {
  stockLot: StockLotCRUDSchema;
  consumedQuant: number;
};

export enum ProductionRunStatusOptions {
  draft = 'Draft',
  in_progress = 'In Progress',
  complete = 'Complete',
  // eslint-disable-next-line no-restricted-syntax -- copy is spelled correctly
  cancelled = 'Canceled',
  approved = 'Approved',
}

export type ProductionRunBomSummary = {
  id: string;
  total: number;
};

export type ProductionRunPart = {
  part: Part;
  alts: Part[];
  unapprovedAlts: Part[];
  id: string;
  quant: number;
  overage: number;
  total: number;
};

export type ProductionRunPartDescribeParts = {
  part: PartCore;
  alts: PartCore[];
  unapprovedAlts: PartCore[];
  id: string;
  quant: number;
  overage: number;
  total: number;
  partType?: OrgPartType;
} & V2OrgPartTypes;

export type ProductionRunPartsDescribeParts = {
  boms: BomLite[];
  bomLines: BomLineLite[];
  bomSummary: ProductionRunBomSummary[];
  partSummary: ProductionRunPartDescribeParts[];
};

export enum ProductionRunPartOverrideStatus {
  MANUFACTURER = 'manufacturer_provided',
  IGNORE = 'ignore',
  OTHER = 'other',
  QUANTITY = 'quantity',
}

export type ProductionRunPartOverride = {
  id: string;
  status: ProductionRunPartOverrideStatus;
  part: Part['id'];
  quant: number;
  productionRun: ProductionRun['id'];
};

export type ProductionRunPartOverrideCreate = {
  status: string;
  part: string;
  quant: number;
  productionRun: string;
};

type ProgramRowLoadingStatusTypes = {
  inventoryLoading: boolean;
  shipmentsLoading: boolean;
  allocationLoading: boolean;
};

export type ProgramRowLoadingSimplifiedStatusTypes = {
  sourcingLoading: boolean;
  buyingLoading: boolean;
  stockLoading: boolean;
  kitLoading: boolean;
};

export enum ProgramRowSourcingStatus {
  notSourced = 'Not Sourced',
  maybe = 'Maybe',
  quotable = 'Quotable',
  inRfq = 'In RFQ',
  inProgress = 'In Progress',
  quotesReceived = 'Quotes Received',
  buyable = 'Buyable',
}

export const ProgramRowSourcingStatusConfig = {
  [ProgramRowSourcingStatus.notSourced]: {
    color: ChipColor.error,
    icon: IconAlertTriangle,
  },
  [ProgramRowSourcingStatus.maybe]: {
    color: ChipColor.warning,
    icon: IconAlertTriangle,
  },
  [ProgramRowSourcingStatus.quotable]: {
    color: ChipColor.warning,
    icon: IconAlertTriangle,
  },
  [ProgramRowSourcingStatus.inRfq]: {
    color: ChipColor.secondary,
    icon: IconSend,
  },
  [ProgramRowSourcingStatus.inProgress]: {
    color: ChipColor.primary,
    icon: IconLoader,
  },
  [ProgramRowSourcingStatus.quotesReceived]: {
    color: ChipColor.primary,
    icon: IconInbox,
  },
  [ProgramRowSourcingStatus.buyable]: {
    color: ChipColor.success,
    icon: IconCheck,
  },
};

export enum ProgramRowBuyingStatus {
  notPurchased = 'Not Purchased',
  inCart = 'In Cart',
  waitingForApproval = 'Waiting for Approval',
  inProgress = 'In Progress',
  ordered = 'Ordered',
  confirmed = 'Confirmed',
  shipped = 'Shipped',
  delivered = 'Delivered',
  received = 'Received',
}

export const ProgramRowBuyingStatusConfig = {
  [ProgramRowBuyingStatus.notPurchased]: {
    color: ChipColor.error,
    icon: IconAlertTriangle,
  },
  [ProgramRowBuyingStatus.inCart]: {
    color: ChipColor.secondary,
    icon: IconShoppingCart,
  },
  [ProgramRowBuyingStatus.waitingForApproval]: {
    color: ChipColor.secondary,
    icon: IconShoppingCartQuestion,
  },
  [ProgramRowBuyingStatus.inProgress]: {
    color: ChipColor.primary,
    icon: IconLoader,
  },
  [ProgramRowBuyingStatus.ordered]: {
    color: ChipColor.primary,
    icon: IconMailShare,
  },
  [ProgramRowBuyingStatus.confirmed]: {
    color: ChipColor.primary,
    icon: IconFileCheck,
  },
  [ProgramRowBuyingStatus.shipped]: {
    color: ChipColor.primary,
    icon: IconTruckDelivery,
  },
  [ProgramRowBuyingStatus.delivered]: {
    color: ChipColor.success,
    icon: IconTrolley,
  },
  [ProgramRowBuyingStatus.received]: {
    color: ChipColor.success,
    icon: IconPackage,
  },
};

export enum ProgramRowStockStatus {
  notInStock = 'Not in Stock',
  availableExpected = 'Expected',
  availableOnHand = 'On Hand',
  allocatedExpected = 'Allocated Expected',
  allocatedOnHand = 'Allocated On Hand',
  consumed = 'Consumed',
  nonInventory = 'Non Inventory',
}

export const ProgramRowStockStatusConfig = {
  [ProgramRowStockStatus.notInStock]: {
    color: ChipColor.error,
    icon: IconAlertTriangle,
  },
  [ProgramRowStockStatus.availableExpected]: {
    color: ChipColor.warning,
    icon: IconTruckDelivery,
  },
  [ProgramRowStockStatus.availableOnHand]: {
    color: ChipColor.warning,
    icon: IconBuildingWarehouse,
  },
  [ProgramRowStockStatus.allocatedExpected]: {
    color: ChipColor.secondary,
    icon: IconBuildingWarehouse,
  },
  [ProgramRowStockStatus.allocatedOnHand]: {
    color: ChipColor.success,
    icon: IconBuildingWarehouse,
  },
  [ProgramRowStockStatus.consumed]: {
    color: ChipColor.success,
    icon: IconCheck,
  },
};

export enum ProgramRowKitStatus {
  notInKit = 'Not in Kit',
  inDraftKit = 'In Draft Kit',
  inKit = 'In Kit',
  packed = 'Packed',
  shipped = 'Shipped',
  delivered = 'Delivered',
  nonInventory = 'Non Inventory',
}

export const ProgramRowKitStatusConfig = {
  [ProgramRowKitStatus.notInKit]: {
    color: ChipColor.error,
    icon: IconAlertTriangle,
  },
  [ProgramRowKitStatus.inDraftKit]: {
    color: ChipColor.grey,
    icon: IconBox,
  },
  [ProgramRowKitStatus.inKit]: {
    color: ChipColor.secondary,
    icon: IconBox,
  },
  [ProgramRowKitStatus.packed]: {
    color: ChipColor.primary,
    icon: IconPackage,
  },
  [ProgramRowKitStatus.shipped]: {
    color: ChipColor.success,
    icon: IconTruckDelivery,
  },
  [ProgramRowKitStatus.delivered]: {
    color: ChipColor.success,
    icon: IconTrolley,
  },
};

export type PurchaseRequestProductionRunSubtype = Lite<
  PurchaseRequestSchema,
  | 'requestedBy'
  | 'approvedBy'
  | 'voidedBy'
  | 'orgSupplier'
  | 'purchaseRequestGroup'
>;

export type AllocationProductionRunSubschema = Lite<
  AllocationSchema,
  'stockLot'
>;

export type PartTableRowDescribeParts = ProductionRunPartDescribeParts & {
  // Common fields between S2P and Simplified Programs
  id: string;
  unallocated?: number;
  lineStatus?: LineStatus;
  supply?: PartSupply;
  specs?: PartSpecs;
  market?: PartMarket;
  altsSupply?: PartSupply[];
  altsMarket?: PartMarket[];
  minimumOverage: number;
  minimumTotal: number;
  currentTotal?: number;
  expected?: number;
  processing?: number;
  allocated?: number;
  onHand?: number;
  allocatedToRun?: number;
  consumed: number;
  stockByFacility?: NoPartInventory[];
  buyable?: number;
  quotable?: number;
  maybe?: number;
  minLead?: number;
  relatedBomLines?: BomLineLite[];
  relatedBoms?: BomLite[];
  dimensionUnit: UnitOfMeasureOptions;
  dimensionClass: DimensionClassOptions;
  dimensionValue?: number;

  // Legacy S2P program fields
  status?: string;
  procurementStatus?: ProcurementStatusLine[];
  stockStatus?: StockStatusLine[];
  shipmentStatus?: ShipmentStatusLine[];
  kitStatus?: KitRequestLineStats['statusCounts'];
  atProductionLocation?: number;
  atProductionLocationProcessing?: number;
  externalMetadata?: { [key: string]: string | number | null };
  loadingStates?: ProgramRowLoadingStatusTypes;

  // Simplifed buying program fields
  minTargetQuantDifference?: number;
  isNonInventoryPart?: boolean;
  simplifiedLoadingStates?: ProgramRowLoadingSimplifiedStatusTypes;
  orderPurchaseRequests?: PurchaseRequestProductionRunSubtype[];
  orderPurchaseRequestGroups?: PurchaseRequestGroupSchemaLite[];
  rfqPurchaseRequests?: PurchaseRequestProductionRunSubtype[];
  rfqPurchaseRequestGroups?: PurchaseRequestGroupSchemaLite[];
  purchaseLines?: PurchaseOrderLineOrderSubtype[];
  purchases?: PurchaseOrderOrderSubtype[];
  supplierQuoteLines?: SupplierQuoteLineSchemaLite[];
  supplierQuotes?: SupplierQuoteOrderSubtype[];
  shipmentLines?: ShipmentLineOrderSubtype[];
  kitRequestLines?: KitRequestLineSchemaLite[];
  kitRequests?: KitRequestSchemaLite[];
  allocations?: AllocationProductionRunSubschema[];
  hideSourcingStatus?: boolean;
  simplifiedSourcingStatus?: {
    status: ProgramRowSourcingStatus;
    quant: number;
    formattedQuant: string;
  }[];
  hideBuyingStatus?: boolean;
  simplifiedBuyingStatus?: {
    status: ProgramRowBuyingStatus;
    quant: number;
    formattedQuant: string;
  }[];
  simplifiedStockStatus?: {
    status: ProgramRowStockStatus;
    quant: number;
    formattedQuant: string;
  }[];
  simplifiedKitStatus?: {
    status: ProgramRowKitStatus;
    quant: number;
    formattedQuant: string;
  }[];
};

export enum LineStatus {
  default = 'Include',
  ignore = 'Ignore',
  fromCM = 'From CM',
  fromOther = 'From Other',
}

export const LineStatusIconMap = {
  [LineStatus.default]: IconCheck,
  [LineStatus.ignore]: IconX,
  [LineStatus.fromCM]: IconTransferIn,
  [LineStatus.fromOther]: IconTransferIn,
};

export enum PartProcurementStatus {
  needToFind = 'Need to Find',
  maybe = 'Maybe',
  quotable = 'Quotable',
  quoteRequested = 'Quote Requested',
  buyable = 'Buyable',
  requested = 'Requested',
  processed = 'Processed',
  confirmed = 'Confirmed',
  shipped = 'Shipped',
  received = 'Received',
  loading = 'Loading',
}

export interface OrderLineByPart extends OrderLineLite {
  order: Order;
}

export interface ShipmentLineByPart {
  line: ShipmentLineLiteExpandedStockLot;
  shipment: ExpandedShipments;
}

export type ProcurementStatusLine = {
  status: PartProcurementStatus;
  quant: number;
  solicitations?: Solicitation[];
  orderLines?: OrderLineByPart[];
  purchaseRequests?: PurchaseRequest[];
  snapshotLines?: OrderSnapshotLine[];
  purchaseOrderLines?: PurchaseOrderLineServerSideSubSchema[];
};

export enum PartStockStatus {
  notInStock = 'Not in Stock',
  expected = 'Expected',
  processing = 'Processing',
  onHand = 'On Hand',
  atProductionProcessing = 'At Production (Processing)',
  atProductionOnHand = 'At Production (On Hand)',
  nonInventory = 'Non Inventory',
}

export type StockStatusLine = {
  status: PartStockStatus;
  quant: number;
};

export enum PartShipmentStatus {
  addedToKit = 'Added to Kit',
  notInShipment = 'Not in Shipment',
  draft = 'Draft',
  requested = 'Requested',
  pending = 'Pending',
  processing = 'Processing',
  picked = 'Picked',
  packed = 'Packed',
  shipped = 'Shipped',
  received = 'Received',
  ingested = 'Ingested',
  complete = 'Complete',
  nonInventory = 'Non Inventory',
}

export type ShipmentStatusLine = {
  status: PartShipmentStatus;
  quant: number;
  shipmentLines: ShipmentLineByPart[];
};

export type ProductionRunDates = {
  purchaseBy: string;
  onHandBy: string;
  shipBy: string;
  executeBy: string;
};

export type ProductionRunAllocationForPartRequest = {
  name?: string;
  part?: PartId;
  v2OrgPart?: V2OrgPartTypes['v2OrgPart'];
  quant: number;
  minimumQuant?: number;
  alts?: PartId[];
  altsV2OrgParts?: V2OrgPartTypes['altV2OrgParts'];
};

export type ProductionRunAllocationForPartPayload = {
  includeExpectedStock?: boolean;
  includeProcessingStock?: boolean;
  includeOnhandStock?: boolean;
  lotMatchingPrioritization?: string;
  limitToFacilities?: StockLocation['id'][];
  requests: ProductionRunAllocationForPartRequest[];
  name?: string;
};

export type ProductionRunAllocationForPart = {
  id: string;
  name: 'string' | null;
  status: string;
  releaseNotes: string | null;
  originalQuant: number;
  quant: number;
  releasedAt: string | null;
  org: Org['id'];
  stockLot: StockLotCRUDSchema['id'];
  releasedBy: string | null;
  relatedRecord: string;
  part?: PartId;
};

export type ConsumeStockForRunPayload = {
  allocation: AllocationCRUDSchema['id'];
  quant: number;
};

export type ProductionRunProps = {
  runData: {
    productionRun?: ProductionRun;
    isFetchingProductionRun: boolean;
    isLoadingProductionRun: boolean;
    isSuccessGetProductionRun: boolean;
  };
  linesData: {
    lines: ProductionRunLinesLite[];
    isLoadingLines: boolean;
  };
  partsData: {
    productionRunParts?: ProductionRunPartsDescribeParts;
    isFetchingProductionRunParts: boolean;
  };
  partOverridesData: {
    productionRunPartOverrides?: ProductionRunPartOverride[];
    isFetchingProductionRunPartOverrides: boolean;
  };
  stockLocationData: {
    stockLocations?: StockLocation[];
    isSuccessStockLocations: boolean;
  };
  row: {
    rowDataDescribeParts: PartTableRowDescribeParts[];
    isFetchingPartsData: boolean;
    solicitations?: Solicitation[];
    isFetchingInventoryProductionResponse: boolean;
    isFetchingShipments: boolean;
  };
  orgData: { activeOrgId?: Org['id']; org?: Org };
  stats: {
    approvedBomCount: number;
    partsToPurchase: number;
    existingShipmentPartsCount: number;
    partsNeedingQuotes: PartTableRowDescribeParts[];
    totalParts: number;
    allocatedPartsCount: number;
    partiallyAllocatedPartsCount: number;
    editingDisabled: boolean;
    bomsWithDifferentTeam?: ProductionRunLinesLite[];
    progressStackBarData?: ProgressStackProps['bars'];
    bomProgressStackBarData?: {
      bomId: string;
      bars: ProgressStackProps['bars'];
    }[];
  };
  consumeEvents: {
    consumeEvents: ConsumeStockEvent[];
    isFetchingConsumeEvents: boolean;
  };
  partSupplyRetryStatus: PartSupplyRetryStatus | null;
  totalNumberPartsWithSupplyLoadFailures: number;
};
